import { motion } from "framer-motion";
import { HomeLayout } from "layouts";
import { useEffect, useState } from "react";
import { gql } from "graphql-request";
import { fetcher, imageUrl } from "utils";
import { withRouter, useRouter } from "next/router";
import Image from "next/legacy/image";
import Link from "next/link";
import Head from "next/head";
import { cocktailFragmentWithOutMeta as cocktailFragment } from "queries/fragments";
import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";
import {
  Section,
  Container,
  FileButton,
  Button,
  ButtonLink,
  Input,
  GalleryCarousel,
  ProductCarousel,
  ToolsCarousel,
  CreateListModal,
} from "components";
import { Cocktail as CocktailComponent } from "components";
import { cocktailTransformer } from "queries/transformers";
import store from "store2";

import { useCart, useSession } from "hooks";
import urlSlug from "url-slug";
import * as cx from "classnames/bind";
import s from "styles/Ambassadors.module.scss";
import g from "styles/Gallery.module.scss";

import { push } from "@socialgouv/matomo-next";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import countryToCurrency from "country-to-currency";
import Cookies from "js-cookie";

const COCKTAIL_COUNT = 8;
const TOOL_COUNT = 17;
const SLIDE_COUNT = 18;

const Ambassador = ({
  ambassador,
  page,
  qKit,
  setShowLoginModal,
  setLoginModalState,
  csrfToken,
  ...props
}) => {
  //const { router } = useRouter();
  const { t } = useTranslation();
  const { locale } = useRouter();
  const [promotedCocktails, setPromotedCocktails] = useState([]);
  const [description, setDescription] = useState(ambassador.description || "");

  const tools = Array.from(Array(TOOL_COUNT).keys());
  const slides = Array.from(Array(SLIDE_COUNT).keys());

  const country = Cookies.get("__country") || "ES";
  const [showCreateListModal, setShowCreateListModal] = useState(false);

  const currency = qKit.prices[(countryToCurrency[country] || "").toLowerCase()]
    ? countryToCurrency[country].toLowerCase()
    : "eur";
  const price = qKit.prices[currency];
  const { data: session, status } = useSession();
  const router = useRouter();

  const { cart, addToCart } = useCart();

  useEffect(() => {
    async function fetchData() {
      const promotedQuery = `query ambassadorRelated @cached(ttl: 300) {
            cocktails(where: {id: {_in: [${ambassadorIds.join(",")}]}}) {
                ${cocktailFragment}
            }}`;
      const response = await fetcher(promotedQuery);
      setPromotedCocktails((response.cocktails || []).map(cocktailTransformer));
    }
    setPromotedCocktails([]);
    const ambassadorIds =
      ((ambassador || {}).extra_data || {}).relatedCocktails || [];
    if (ambassadorIds.length > 0) {
      fetchData();
    }
  }, [ambassador]);

  const extractCocktails = (obj, cocktails = []) => {
    if (obj) {
      cocktails = cocktails.concat(obj.cocktails || []);
      cocktails = extractCocktails(obj.parent, cocktails);
    }
    return cocktails;
  };

  useEffect(() => {
    if (window) {
      window.addEventListener("showLoginModal", (event) => {
        setLoginModalState({ index: 0, show: true });
      });
    }
  }, []);
  const onAddToCart = async () => {
    push([
      "trackEvent",
      "Ecommerce",
      "Add to Cart",
      `Add to cart -  ${ambassador.name}`,
    ]);
    push([
      "addEcommerceItem",
      qKit.id,
      "Q-kit - 17 essential tools for your home bar set",
      4990.0,
    ]);
    push(["trackEcommerceCartUpdate", 4990.0]);
    await addToCart(qKit, currency);
    router.push("/cart");
  };

  const afterRegister = () => {
    push([
      "trackEvent",
      "Ambassador",
      "Register",
      `Register -  ${ambassador.name}`,
    ]);
    setButtonClass("Hollow Green disabled");
    setCodeSent(true);
  };
  const [buttonClass, setButtonClass] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  const handleClick = async () => {
    const url = process.env.NEXT_PUBLIC_REST_API_URL + "send_discount_email";
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Locale: router.locale,
      },
      body: JSON.stringify({
        ambassador_id: ambassador.id,
        email: session.user.email,
      }),
    });
    push([
      "trackEvent",
      "Ambassador",
      "Get discount",
      `Get discount -  ${ambassador.name}`,
    ]);
    setButtonClass("Hollow Green disabled");
    setCodeSent(true);
  };

  function renderSafeHTML(html) {
    return { __html: html };
  }

  return (
    <>
      <Head>
        <title key="title">{ambassador.name} - Cocktail Club Ambassador</title>
        {ambassador.description && (
          <meta
            key="description"
            name="description"
            content={ambassador.description.replace(regex, "")}
          />
        )}
        <meta
          key="ogUrl"
          property="og:url"
          content={"https://cocktailclub.com/ambassadors/" + ambassador.slug}
        />
        <link
          key="canonical"
          rel="canonical"
          href={"https://cocktailclub.com/ambassadors/" + ambassador.slug}
        />

        <meta key="ogType" property="og:type" content="article" />
        <meta
          key="ogTitle"
          property="og:title"
          content={`${ambassador.name} - Cocktail Club Ambassador `}
        />
        {ambassador.description && (
          <meta
            key="ogDescription"
            property="og:description"
            content={ambassador.description.replace(regex, "")}
          />
        )}
        <meta
          key="ogImage"
          property="og:image"
          content={imageUrl(ambassador.image_id || "")}
        />
        <meta
          property="og:image:alt"
          content={`${ambassador.name} - Cocktail Club Journal `}
        />

        <meta property="article:section" content="Ambassador" />
        <meta
          property="article:tag"
          content={`${ambassador.name} - Cocktail Club Ambassador`}
        />
      </Head>
      <article itemScope itemType="http://schema.org/Article">
        <Section>
          <Container>
            <div className="flex flex-wrap row-reverse__m Rad ofh Shadow">
              <div className="split-item-50__d flex align-center p32 b-white">
                <div className={cx(s.contentInner, "contentInner")}>
                  <h1 className="Heading__main">{ambassador.title}</h1>
                  {/*
                <p
                  className=""
                  itemProp="description"
                  dangerouslySetInnerHTML={{ __html: description || "" }}
                ></p>
                */}
                  <p
                    className={cx(s.ambassadorContent)}
                    itemProp="description"
                    dangerouslySetInnerHTML={renderSafeHTML(
                      ambassador.translations[locale].description ||
                        ambassador.translations.en.description,
                    )}
                  ></p>
                </div>
              </div>
              <div className="split-item-50__d">
                <div class="imgRes imgResPage pb100">
                  <img
                    objectfit="cover"
                    src={imageUrl(ambassador.image_id || "")}
                  />
                </div>
              </div>
            </div>
          </Container>
        </Section>
        <Section>
          <Container>
            <div
              className={cx(
                s.AmbDiscount,
                "flex flex-wrap justify-center align-center b-white px32 py24 Rad Shadow",
              )}
            >
              <h2 className="Heading__sub Bold m0 flex-grow">
                {t(
                  "Sign up to Cocktail Club to generate your personal discount code",
                )}
              </h2>
              {session?.user ? (
                <Button
                  onClick={handleClick}
                  className={`Primary Wide Cyan  ${buttonClass}`}
                >
                  {!codeSent
                    ? t("Get your discount code")
                    : t("Code sent to your email")}
                </Button>
              ) : (
                <Button
                  className={cx("Primary Wide Cyan")}
                  onClick={() =>
                    setLoginModalState({
                      show: true,
                      message: t(
                        "Please register to receive your discount code by email",
                      ),
                      index: 1,
                      ambassador,
                      afterRegister,
                    })
                  }
                >
                  {!codeSent ? t("Sign up") : t("Code sent to your email")}
                </Button>
              )}
            </div>
          </Container>
        </Section>
        <Section>
          <Container>
            <div
              className={cx(s.Qkit, "flex flex-wrap b-white Rad Shadow ofh")}
            >
              <div className={cx(s.QKit_images)}>
                <ProductCarousel noThumbs slides={slides} />
              </div>
              <div className={cx(s.QKit_info, "p32")}>
                <h2 className="Heading__sub Bold">
                  {t("Bar set / 17 essential tools for your home bar set")}
                </h2>
                <br />
                <p className="Heading__price_small Cyan">
                  <bdi>
                    {price.unit_amount / 100.0}
                    &nbsp;
                    <span>{currency.toUpperCase()}</span>
                  </bdi>
                </p>
                <small>{t("Local tax may apply on delivery")}</small>
                <hr />
                <h2>
                  {t(
                    "The Cocktail Club bar set is the most comprehensive cocktail making solution on the market – bundled with our step-by-step video recipes",
                  )}
                </h2>
                <ul>
                  <li>
                    <p>
                      {t(
                        "Everything you need to mix any cocktail – bar toolset and recipes",
                      )}
                    </p>
                  </li>
                  <li>
                    <p>
                      {t(
                        "17 must-have premium tools make up the ultimate home bar set",
                      )}
                    </p>
                  </li>
                  <li>
                    <p>
                      {t(
                        "Features a washable roll-bag, cutting board and ice bucket",
                      )}
                    </p>
                  </li>
                  <li>
                    <p>
                      {t("High-end Nordic design with portability in mind")}
                    </p>
                  </li>
                  <li>
                    <p>
                      {t(
                        "The Q-Kit bar set is integrated with our bottle scanner and recipe app",
                      )}
                    </p>
                  </li>
                </ul>
                <br />
                <p className="Bold mb0">
                  <small>
                    {t("Free shipping")}: {t("3 - 5 days")}
                  </small>
                </p>
                <div
                  className="Button Primary Full mt18"
                  size="far"
                  svg="shopping-cart"
                  onClick={onAddToCart}
                >
                  {t("Add to cart")}
                </div>
              </div>
            </div>
          </Container>
        </Section>
        <Section>
          <Container>
            <h2 className="Heading mb8">
              {t("Tools included in the bar set")}
            </h2>
            <br />
            {/*<p>{t("See all 17 premium bar tools")}</p>*/}
            <ToolsCarousel tools6 radius tools={tools} />
          </Container>
        </Section>
        {/*
        {ambassador.embed && (
          <div
            className={cx("PageEmbedContent", s.PageEmbedContent)}
            dangerouslySetInnerHTML={{
              __html: page.embed,
            }}
          />
        )}
        */}
        <Section>
          <Container>
            {promotedCocktails.length > 0 ? (
              <>
                <h2 className="Heading mb24">
                  {ambassador.handle} {t("specials")}
                </h2>
                <motion.div
                  className={cx("GalleryFeed row", g.GalleryFeed)}
                  layout
                >
                  {(promotedCocktails || []).map((cocktail) => (
                    <CocktailComponent
                      key={cocktail.id}
                      cocktail={cocktail}
                      setShowCreateListModal={setShowCreateListModal}
                      setShowLoginModal={props.setShowLoginModal}
                      initial={false}
                      layout={false}
                      {...props}
                    />
                  ))}
                </motion.div>
                <br />
                <br />
                <br />
              </>
            ) : (
              <></>
            )}
          </Container>
        </Section>
        <CreateListModal
          onClose={() => setShowCreateListModal(false)}
          show={showCreateListModal}
        />
      </article>
    </>
  );
};

export async function getStaticProps({ params, locale }) {
  const products = await fetch(
    `${process.env.NEXT_PUBLIC_REST_API_URL}stripe/products`,
  ).then((r) => r.json());

  const query = gql`{
    ambassadors(where: {slug: {_eq: "${params.slug}"}}, limit: 1) {
      id
      title
      slug
      handle
      name
      description
      extra_data
      hidden
      translations
      image_id
    }
  }`;

  const data = await fetcher(query);
  const ambassador = data.ambassadors.length > 0 ? data.ambassadors[0] : null;

  if (!ambassador) {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    };
  }

  const translations = await serverSideTranslations(locale);

  return {
    revalidate: 60,
    props: {
      ...translations,
      qKit:
        products[process.env.NEXT_PUBLIC_Q_KIT_STRIPE_ID] ||
        products[Object.keys(products)[0]],
      ambassador,
    },
  };
}

Ambassador.getLayout = (page) => {
  return <HomeLayout className={cx("Page", s.Page)}>{page}</HomeLayout>;
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

//export default withRouter(Page);
export default Ambassador;
